import Vue from 'vue';
import Buefy from 'buefy';
// import "buefy/dist/buefy.css";
import './style.scss';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import NProgress from 'vue-nprogress';

import App from './App.vue';
import router from './router';
import store from './store';
import './filters';
import BreadcrumbList from './components/BreadcrumbList';
import BreadcrumbLink from './components/BreadcrumbLink';

// Global components
Vue.component('BreadcrumbLink', BreadcrumbLink);
Vue.component('BreadcrumbList', BreadcrumbList);

// Fix ResizeObserver loop limit exceeded error for Buefy table
const fixBuefyTableResizeObserver = () => {
  const originalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends originalResizeObserver {
    constructor(callback) {
      let timeoutId;
      const debouncedCallback = (...args) => {
        if (timeoutId) {
          window.cancelAnimationFrame(timeoutId);
        }
        timeoutId = window.requestAnimationFrame(() => {
          callback.apply(this, args);
        });
      };
      super(debouncedCallback);
    }
  };
};

// Apply the fix before Vue initialization
fixBuefyTableResizeObserver();

Sentry.init({
  Vue: Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  environment: process.env.VUE_APP_ENVIRONMENT,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: [
        'dev-app.quickadmit.com',
        'staging-app.quickadmit.com',
        'app.quickadmit.com',
        /^\//,
      ],
    }),
  ],
  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 1.0,
  // Ignore navigation guard redirects which are intentional
  beforeSend(event) {
    // Ignore navigation guard redirects that are intentional authentication redirects
    if (event.exception && event.exception.values) {
      const exception = event.exception.values[0];
      if (
        exception.type === 'Error' &&
        exception.value &&
        (exception.value.includes('Redirected when going from') ||
          exception.value.includes('Navigation cancelled') ||
          exception.value.includes('Navigation aborted') ||
          exception.value.includes('Navigation cancelled from') ||
          exception.value.includes('with a new navigation') ||
          exception.value.includes('Request aborted'))
      ) {
        return null; // Don't send this event to Sentry
      }
    }

    // Also ignore navigation-related errors that are intentional
    if (
      event.message &&
      (event.message.includes('Redirected when going from') ||
        event.message.includes('Navigation cancelled') ||
        event.message.includes('Navigation aborted') ||
        event.message.includes('Navigation cancelled from') ||
        event.message.includes('with a new navigation') ||
        event.message.includes('Request aborted'))
    ) {
      return null;
    }

    // Ignore Axios request abort errors
    if (event.exception && event.exception.values) {
      const exception = event.exception.values[0];
      if (
        exception.type === 'AxiosError' &&
        exception.value &&
        exception.value.includes('Request aborted')
      ) {
        return null; // Don't send this event to Sentry
      }
    }

    // Ignore navigation guard redirect errors
    if (event.exception && event.exception.values) {
      const exception = event.exception.values[0];
      if (
        exception.type === 'Error' &&
        exception.value &&
        exception.value.includes('Redirected when going from') &&
        exception.value.includes('via a navigation guard')
      ) {
        return null; // Don't send this event to Sentry
      }
    }

    return event;
  },
});

Vue.config.productionTip = false;
Vue.use(NProgress);
Vue.use(Buefy, { defaultFieldLabelPosition: 'inside' });

const nprogress = new NProgress();

new Vue({
  router,
  store,
  nprogress,
  render: (h) => h(App),
}).$mount('#app');

// Global error handler to catch navigation-related errors
Vue.config.errorHandler = (error, vm, info) => {
  // Ignore navigation guard redirects and other intentional navigation errors
  if (
    error &&
    error.message &&
    (error.message.includes('Redirected when going from') ||
      error.message.includes('Navigation cancelled') ||
      error.message.includes('Navigation aborted') ||
      error.message.includes('NavigationDuplicated') ||
      error.message.includes('Navigation cancelled from') ||
      error.message.includes('with a new navigation') ||
      error.message.includes('Request aborted'))
  ) {
    return; // Don't report these to Sentry
  }

  // Ignore Axios request abort errors
  if (
    error &&
    error.name === 'AxiosError' &&
    error.message &&
    error.message.includes('Request aborted')
  ) {
    return; // Don't report these to Sentry
  }

  // Ignore navigation guard redirect errors
  if (
    error &&
    error.message &&
    error.message.includes('Redirected when going from') &&
    error.message.includes('via a navigation guard')
  ) {
    return; // Don't report these to Sentry
  }

  // For other errors, let Sentry handle them
  Sentry.captureException(error, {
    extra: {
      componentName: vm?.$options?.name || 'Unknown',
      info: info,
    },
  });
};

// Handle unhandled promise rejections (like navigation errors)
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;

  // Ignore navigation-related promise rejections
  if (
    error &&
    error.message &&
    (error.message.includes('Navigation cancelled') ||
      error.message.includes('Navigation aborted') ||
      error.message.includes('NavigationDuplicated') ||
      error.message.includes('Navigation cancelled from') ||
      error.message.includes('with a new navigation') ||
      error.message.includes('Request aborted'))
  ) {
    event.preventDefault(); // Prevent the error from being logged to console
    return;
  }

  // Ignore Axios request abort errors
  if (
    error &&
    error.name === 'AxiosError' &&
    error.message &&
    error.message.includes('Request aborted')
  ) {
    event.preventDefault(); // Prevent the error from being logged to console
    return;
  }

  // Ignore navigation guard redirect errors
  if (
    error &&
    error.message &&
    error.message.includes('Redirected when going from') &&
    error.message.includes('via a navigation guard')
  ) {
    event.preventDefault(); // Prevent the error from being logged to console
    return;
  }

  // For other unhandled rejections, let Sentry handle them
  Sentry.captureException(error);
});

// FIXME: Used for new version. Move this code some where else.
Object.defineProperty(String.prototype, 'hashCode', {
  value: function () {
    var hash = 0,
      i,
      chr;
    for (i = 0; i < this.length; i++) {
      chr = this.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  },
});
